import Big from 'big.js';

function quantityExponent(val) {
	if (val === 0) {
		return 0;
	}

	let exp = Math.floor(Math.log(val) / Math.LN10);
	/**
	 * exp is expected to be the rounded-down result of the base-10 log of val.
	 * But due to the precision loss with Math.log(val), we need to restore it
	 * using 10^exp to make sure we can get val back from exp. #11249
	 */
	if (val / Math.pow(10, exp) >= 10) {
		exp++;
	}
	// minimum 0.01
	return Math.max(-2, exp);
}

function nice(val) {
	const exponent = quantityExponent(val);
	const exp10 = Math.pow(10, exponent);
	const f = new Big(val).div(exp10).toNumber(); // 1 <= f < 10
	let nf;
	if (f < 1) {
		nf = 1;
	} else if (f < 1.5) {
		nf = 1.5;
	} else if (f < 2) {
		nf = 2;
	} else if (f < 2.5) {
		nf = 2.5;
	} else if (f < 3) {
		nf = 3;
	} else if (f < 5) {
		nf = 5;
	} else if (f < 6) {
		nf = 6;
	} else if (f < 7.5) {
		nf = 7.5;
	} else if (f < 8) {
		nf = 8;
	} else {
		nf = 10;
	}
	let interval = nf * exp10;

	// Fix 3 * 0.1 === 0.30000000000000004 issue (see IEEE 754).
	// 20 is the uppper bound of toFixed.
	return exponent >= -20 ? +interval.toFixed(exponent < 0 ? -exponent : 1) : interval;
}

function increaseInterval(interval) {
	const exp10 = Math.pow(10, quantityExponent(interval));
	// Increase interval
	let f = new Big(interval).div(exp10).toNumber();
	if (!f) {
		f = 1;
	} else if (f === 1) {
		f = 1.5;
	} else if (f === 1.5) {
		f = 2;
	} else if (f === 2) {
		f = 2.5;
	} else if (f === 2.5) {
		f = 3;
	} else if (f === 3) {
		f = 5;
	} else if (f === 5) {
		f = 6;
	} else if (f === 6) {
		f = 7.5;
	} else if (f === 7.5) {
		f = 8;
	} else {
		f = 10;
	}
	return f * exp10;
}

/**
 * @param extent Both extent[0] and extent[1] should be valid number.
 *               Should be extent[0] < extent[1].
 * @param splitNumber splitNumber should be >= 1.
 */
export function calcNiceTicks(extent, splitNumber = 6) {
	if (extent.some(num => [Infinity, -Infinity, NaN].includes(num))) return {};
	if (extent[0] === extent[1]) {
		if (extent[0] > 0) {
			extent[0] = 0;
		} else {
			extent[1] = 0;
		}
	}
	const _splitNumber = Math.max(1, splitNumber - 1);
	let interval = nice((extent[1] - extent[0]) / _splitNumber);
	// Niced extent outside original extent
	const niceTickExtent = [Math.floor(extent[0] / interval) * interval, Math.ceil(extent[1] / interval) * interval];

	if (niceTickExtent[0] <= extent[0]) {
		let max = niceTickExtent[0] + _splitNumber * interval;
		while (max < extent[1] && isFinite(max)) {
			interval = increaseInterval(interval);
			max = niceTickExtent[0] + interval * _splitNumber;
		}
		niceTickExtent[1] = max;
	} else {
		let min = niceTickExtent[1] - _splitNumber * interval;
		while (min > extent[0] && isFinite(min)) {
			interval = increaseInterval(interval);
			min = niceTickExtent[1] - _splitNumber * interval;
		}
		niceTickExtent[0] = min;
	}

	return {
		interval,
		min: +niceTickExtent[0].toFixed(2),
		max: +niceTickExtent[1].toFixed(2)
	};
}
